import { lazy, type ComponentType } from "react";

// A lazy chunk can fail to import when the browser holds a stale reference to a
// module that no longer exists (after a redeploy or a dev-server restart). The
// symptom is "TypeError: Importing a module script failed." and the screen goes
// blank. Reloading once picks up the current module graph and fixes it; the
// sessionStorage guard keeps a genuinely broken module from looping forever.
const RELOAD_KEY = "oli:chunk-reloaded";

const canStore = () => {
  try {
    return typeof window !== "undefined" && !!window.sessionStorage;
  } catch {
    return false;
  }
};

const readFlag = () => {
  try {
    return canStore() ? window.sessionStorage.getItem(RELOAD_KEY) : null;
  } catch {
    return null;
  }
};

const writeFlag = () => {
  try {
    if (canStore()) window.sessionStorage.setItem(RELOAD_KEY, "1");
  } catch {
    /* storage unavailable — fall through to the error boundary */
  }
};

const clearFlag = () => {
  try {
    if (canStore()) window.sessionStorage.removeItem(RELOAD_KEY);
  } catch {
    /* ignore */
  }
};

export async function importWithRetry<T extends ComponentType<unknown>>(
  factory: () => Promise<{ default: T }>
): Promise<{ default: T }> {
  try {
    const mod = await factory();
    clearFlag();
    return mod;
  } catch (err) {
    if (!readFlag()) {
      writeFlag();
      window.location.reload();
      // Keep the current fallback visible while the reload takes effect.
      return new Promise<{ default: T }>(() => {});
    }
    throw err;
  }
}

export function lazyWithRetry<T extends ComponentType<unknown>>(
  factory: () => Promise<{ default: T }>
) {
  return lazy(() => importWithRetry(factory));
}
